import { parseQuery } from '@trm/ui-kit-admin/query-search';
import { Match } from 'effect';
import type { ParsedTraderRow } from '~/utils/parseTraderCsv';
import { isPositiveGraphqlId } from '~/utils/queryFilterOperators';

import type { UserFilter } from '../../../../../../generated/types';
import { toUserFilter } from './userQueryFilter';
import { usersQuery, usersQuerySchema } from './usersQuerySchema';

export interface UserSearchState {
  readonly search: string;
  readonly csvRows: readonly ParsedTraderRow[] | null;
}

type Clause = UserFilter;

export const isUserId = isPositiveGraphqlId;

const searchClause = (search: string): Clause | null => {
  if (search.trim() === '') return null;
  const parsed = parseQuery(search, usersQuerySchema);
  return Match.value(parsed).pipe(
    Match.when({ kind: 'text' }, ({ text }) => usersQuery.text(text) ?? null),
    Match.when({ kind: 'valid' }, ({ expression }) => toUserFilter(expression)),
    Match.when({ kind: 'incomplete' }, (): Clause => ({ rowId: { in: [] } })),
    Match.when({ kind: 'invalid' }, (): Clause => ({ rowId: { in: [] } })),
    Match.exhaustive,
  );
};

export const commitsImmediately = (search: string): boolean => {
  const parsed = parseQuery(search, usersQuerySchema);
  return parsed.kind === 'text' || parsed.kind === 'valid';
};

const rowClause = (row: ParsedTraderRow): Clause | null => {
  if (row.userId !== undefined && !isUserId(row.userId)) return null;

  const identifiers: Clause[] = [
    row.userId === undefined ? null : { rowId: { equalTo: Number(row.userId) } },
    row.email ? { email: { inInsensitive: [row.email] } } : null,
    row.username ? { userContactInfo: { screenName: { inInsensitive: [row.username] } } } : null,
  ].filter((clause) => clause !== null);

  if (identifiers.length === 0) return null;
  return identifiers.length === 1 ? identifiers[0] : { and: identifiers };
};

const csvClause = (rows: readonly ParsedTraderRow[]): Clause => {
  const alternatives = rows.map(rowClause).filter((clause): clause is Clause => clause !== null);

  return alternatives.length > 0 ? { or: alternatives } : { rowId: { in: [] } };
};

export const buildUserFilter = ({ search, csvRows }: UserSearchState): Clause | undefined => {
  const clauses = [csvRows === null ? null : csvClause(csvRows), searchClause(search)].filter(
    (clause): clause is Clause => clause !== null,
  );

  if (clauses.length === 0) return undefined;
  return clauses.length === 1 ? clauses[0] : { and: clauses };
};
